import React, { useEffect, useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { useUserId } from "../UserIdContext";
import "./usuarios.css";

const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3220",
  headers: { "Content-Type": "application/json" },
});

const fmtDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const getClienteTexto = (ingreso) =>
  ingreso?.cliente?.nombreCliente ||
  ingreso?.cliente?.codCliente ||
  ingreso?.cliente?.CUIT ||
  "Cliente sin nombre";

const getRemitoTexto = (ingreso) => ingreso?.nroRemito || ingreso?.remito || "-";

export default function RemitosAnulados() {
  const { deposito } = useUserId();
  const depositoActual = deposito || "binder1";

  const [filters, setFilters] = useState({
    remito: "",
    cliente: "",
    desde: "",
    hasta: "",
  });
  const [ingresos, setIngresos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [total, setTotal] = useState(0);

  const setFeedback = (text, tone = "info") => {
    setMensaje({ text, tone, ts: Date.now() });
    setTimeout(() => setMensaje(null), 4500);
  };

  const buscarAnulados = async (override) => {
    const data = override || filters;
    setLoading(true);
    try {
      const params = {};
      if (data.remito.trim()) params.remito = data.remito.trim();
      if (data.cliente.trim()) params.cliente = data.cliente.trim();
      if (data.desde) params.desde = data.desde;
      if (data.hasta) params.hasta = data.hasta;
      params.limit = 60;
      params.deposito = depositoActual;
      const resp = await api.get("/api/anular/anulados", { params });
      const list = Array.isArray(resp?.data?.data?.ingresos)
        ? resp.data.data.ingresos
        : [];
      setIngresos(list);
      setTotal(resp?.data?.data?.total ?? list.length);
      if (!list.length) {
        setFeedback("Sin resultados", "warn");
      }
    } catch (err) {
      setFeedback(
        err?.response?.data?.error || "No pude obtener remitos anulados",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscarAnulados();
  }, [depositoActual]);

  const handleInput = (field) => (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleLimpiar = () => {
    const next = { remito: "", cliente: "", desde: "", hasta: "" };
    setFilters(next);
    buscarAnulados(next);
  };

  return (
    <div className="users-wrap">
      <div className="users-header">
        <div className="title-stack">
          <h1>Remitos anulados</h1>
          <p className="subtitle">
            Filtra por remito, cliente o fechas de anulacion
          </p>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="form-grid drawer-grid">
          <div className="field">
            <label>Nro de remito</label>
            <input
              value={filters.remito}
              onChange={handleInput("remito")}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscarAnulados();
              }}
              placeholder="Ej: R-000123"
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="field">
            <label>Cliente</label>
            <input
              value={filters.cliente}
              onChange={handleInput("cliente")}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscarAnulados();
              }}
              placeholder="Nombre, codigo o CUIT"
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="field">
            <label>Desde</label>
            <input
              type="date"
              value={filters.desde}
              onChange={handleInput("desde")}
            />
          </div>
          <div className="field">
            <label>Hasta</label>
            <input
              type="date"
              value={filters.hasta}
              onChange={handleInput("hasta")}
            />
          </div>
          <div className="field" style={{ alignSelf: "flex-end" }}>
            <button
              type="button"
              className="btn solid"
              onClick={() => buscarAnulados()}
              disabled={loading}
            >
              {loading ? "Buscando..." : "Buscar"}
            </button>
          </div>
          <div className="field" style={{ alignSelf: "flex-end" }}>
            <button
              type="button"
              className="btn"
              onClick={handleLimpiar}
              disabled={loading}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {mensaje && (
        <div
          className="panel"
          style={{
            marginBottom: 12,
            borderColor:
              mensaje.tone === "error"
                ? "#5a2d2d"
                : mensaje.tone === "warn"
                ? "#735d24"
                : "#1a4f42",
          }}
        >
          {mensaje.text}
        </div>
      )}

      <div className="panel table-panel">
        <div className="table-title">
          <div className="title-left">
            Resultados: <strong>{total}</strong>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Remito</th>
                <th>Cliente</th>
                <th>Fecha remito</th>
                <th>Fecha anulacion</th>
                <th>Motivo</th>
                <th className="mono">Pallets</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="empty">
                    Cargando remitos anulados...
                  </td>
                </tr>
              ) : ingresos.length ? (
                ingresos.map((ingreso) => {
                  const pallets = Array.isArray(ingreso.pallets)
                    ? ingreso.pallets
                    : [];
                  return (
                    <tr key={ingreso._id}>
                      <td>{getRemitoTexto(ingreso)}</td>
                      <td>{getClienteTexto(ingreso)}</td>
                      <td>{fmtDate(ingreso.fechaRemito)}</td>
                      <td>{fmtDate(ingreso.fechaAnulacion)}</td>
                      <td>{ingreso.motivoAnulacion || "-"}</td>
                      <td className="mono">{pallets.length}</td>
                      <td>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setDetalle(ingreso)}
                          title="Ver detalle"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="empty">
                    Sin remitos anulados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detalle && (
        <div className="modal-backdrop" onClick={() => setDetalle(null)}>
          <div className="modal panel" onClick={(e) => e.stopPropagation()}>
            <div
              className="section-header"
              style={{ justifyContent: "space-between", width: "100%" }}
            >
              <div className="title-stack">
                <h2 style={{ margin: 0 }}>
                  Remito {getRemitoTexto(detalle)}
                </h2>
                <p className="subtitle">
                  Cliente: {getClienteTexto(detalle)}
                </p>
              </div>
              <span className="badge ok">anulado</span>
            </div>

            <div className="section" style={{ marginTop: 8 }}>
              <div className="row-line">
                Fecha remito: {fmtDate(detalle.fechaRemito) || "-"}
              </div>
              <div className="row-line">
                Fecha anulacion: {fmtDate(detalle.fechaAnulacion) || "-"}
              </div>
              <div className="row-line">
                Motivo: {detalle.motivoAnulacion || "-"}
              </div>
            </div>

            <div className="section">
              <div className="section-header">
                <h3 style={{ margin: 0 }}>Pallets</h3>
              </div>
              <div className="table-responsive">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>Nro pallet</th>
                      <th>Identificacion</th>
                      <th>Producto</th>
                      <th>Lote</th>
                      <th className="mono">Cantidad</th>
                      <th>Condicion</th>
                      <th>F. elab</th>
                      <th>F. vto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(detalle.pallets) &&
                    detalle.pallets.length ? (
                      detalle.pallets.map((p) => (
                        <tr key={p._id || p.nPallet}>
                          <td>{p.nPallet || "-"}</td>
                          <td>{p.identificacion || "-"}</td>
                          <td>
                            {p.detalleProductoTexto ||
                              p.fcGrupoProveedor ||
                              p.codigo ||
                              "-"}
                          </td>
                          <td>{p.loteTexto || "-"}</td>
                          <td className="mono">
                            {Number.isFinite(+p.cantidad)
                              ? p.cantidad
                              : p.unidadesPorPallet || 0}
                          </td>
                          <td>{p.condicion || "-"}</td>
                          <td>{fmtDate(p.fechaElaboracion)}</td>
                          <td>{fmtDate(p.fechaVencimiento)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="empty">
                          Sin pallets asociados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setDetalle(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
